"use client";

import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import CookieSessionManager from "@/app/services/session.client";
import { AppConstants } from "@/app/lib/util/AppConstants";
import { goToPage } from "@/app/lib/util/utilities.client";
import Cookies from "js-cookie";
import logger from "@/lib/logger";

/** Poll interval for idle checks (setTimeout cannot exceed ~24.8 days in browsers). */
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;

interface UseIdleTimeoutOptions {
  /**
   * Idle timeout in minutes (default: 30 minutes)
   */
  timeoutMinutes?: number;
  /**
   * Warning time in minutes before logout (default: 5 minutes)
   * Set to 0 to disable warning
   */
  warningMinutes?: number;
  /**
   * Callback when warning is triggered
   */
  onWarning?: (remainingMinutes: number) => void;
  /**
   * Callback when logout is triggered
   */
  onLogout?: () => void;
  /**
   * Whether the idle timer is enabled (default: true)
   */
  enabled?: boolean;
}

/**
 * Automatically log out users after a period of inactivity.
 * Uses timestamp polling so timeouts longer than ~25 days work reliably.
 */
export const useIdleTimeout = (options: UseIdleTimeoutOptions = {}) => {
  const {
    timeoutMinutes = 30,
    warningMinutes = 5,
    onWarning,
    onLogout,
    enabled = true,
  } = options;

  const router = useRouter();
  const lastActivityRef = useRef<number>(Date.now());
  const isWarningShownRef = useRef<boolean>(false);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;
  const warningThresholdMs = Math.max(0, timeoutMs - warningMs);

  const isCookieExpired = useCallback((): boolean => {
    try {
      const allCookies = Cookies.get();
      for (const cookieName in allCookies) {
        if (Object.values(AppConstants.Roles).includes(cookieName)) {
          const cookieData = CookieSessionManager.getLoginCookie(cookieName);
          if (cookieData?.expires && cookieData.expires < Date.now()) {
            return true;
          }
        }
      }
    } catch (error) {
      logger.error("Error checking cookie expiration:", {
        error: String(error),
      });
    }
    return false;
  }, []);

  const handleLogout = useCallback(async () => {
    logger.info("Idle timeout reached - logging out user");

    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    onLogout?.();
    await CookieSessionManager.doLogout();
    goToPage(router, AppConstants.RouteUrls.LOGIN);
  }, [router, onLogout]);

  const handleWarning = useCallback(
    (remainingMs: number) => {
      const remainingMinutes = Math.max(
        1,
        Math.ceil(remainingMs / (60 * 1000)),
      );
      logger.info(
        `Idle warning: User will be logged out in ${remainingMinutes} minutes`,
      );

      if (onWarning && !isWarningShownRef.current) {
        isWarningShownRef.current = true;
        onWarning(remainingMinutes);
      }
    },
    [onWarning],
  );

  const evaluateIdleState = useCallback(() => {
    if (!enabled) {
      return;
    }

    const idleMs = Date.now() - lastActivityRef.current;

    if (idleMs >= timeoutMs) {
      void handleLogout();
      return;
    }

    if (warningMinutes > 0 && idleMs >= warningThresholdMs) {
      handleWarning(timeoutMs - idleMs);
    }
  }, [
    enabled,
    handleLogout,
    handleWarning,
    timeoutMs,
    warningMinutes,
    warningThresholdMs,
  ]);

  const resetIdleTimer = useCallback(() => {
    if (!enabled) {
      return;
    }

    lastActivityRef.current = Date.now();
    isWarningShownRef.current = false;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    lastActivityRef.current = Date.now();
    isWarningShownRef.current = false;

    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
      "focus",
    ] as const;

    const handleActivity = () => {
      resetIdleTimer();
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    evaluateIdleState();
    checkIntervalRef.current = setInterval(
      evaluateIdleState,
      IDLE_CHECK_INTERVAL_MS,
    );

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [enabled, evaluateIdleState, resetIdleTimer]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const checkCookieInterval = setInterval(
      () => {
        if (isCookieExpired()) {
          logger.info("Cookie expired - logging out user");
          void handleLogout();
        }
      },
      5 * 60 * 1000,
    );

    return () => {
      clearInterval(checkCookieInterval);
    };
  }, [enabled, isCookieExpired, handleLogout]);

  return {
    resetIdleTimer,
    lastActivity: lastActivityRef.current,
  };
};

export default useIdleTimeout;
